import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useToasts } from '../../hooks/useToasts';
import { changePassword } from '../../lib/api/auth';
import { errorMessage } from '../../lib/errors';
import { PASSWORD_MIN_LENGTH, passwordSchema } from '../../lib/password';
import { Button, Notice } from '../../components/primitives';
import { PasswordField } from '../../components/form';
import { PasswordStrengthMeter } from '../../components/PasswordStrength';
import { AuthShell } from './AuthShell';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Ingrese su contraseña actual.'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Repita la nueva contraseña.'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Las contraseñas no coinciden.',
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    path: ['newPassword'],
    message: 'La nueva contraseña debe ser distinta de la actual.',
  });

type ChangePasswordForm = z.infer<typeof schema>;

/**
 * Cambio de contraseña.
 *
 * Cuando `user.mustChangePassword` es true esta pantalla es la única accesible: el
 * enrutador redirige aquí toda navegación hasta que el cambio se complete.
 */
export function ChangePasswordPage(): JSX.Element {
  const { user, refreshUser } = useAuth();
  const { push } = useToasts();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword');
  const forced = user?.mustChangePassword === true;

  const onSubmit = async (values: ChangePasswordForm): Promise<void> => {
    setFormError(null);
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      await refreshUser();
      push({
        kind: 'success',
        title: 'Contraseña actualizada',
        message: 'Su nueva contraseña ya está activa.',
      });
      navigate('/', { replace: true });
    } catch (error) {
      setFormError(errorMessage(error));
    }
  };

  return (
    <AuthShell
      title={forced ? 'Cambio de contraseña obligatorio' : 'Cambiar contraseña'}
      subtitle={
        forced
          ? 'Su cuenta tiene una contraseña provisional. Debe reemplazarla antes de continuar.'
          : 'Defina una contraseña nueva que cumpla las reglas indicadas.'
      }
    >
      <form className="auth__form" onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        {forced && (
          <Notice tone="info" role="status">
            Mientras la contraseña provisional siga vigente, el resto de la plataforma permanece
            bloqueado.
          </Notice>
        )}
        {formError !== null && (
          <Notice tone="error" role="alert">
            {formError}
          </Notice>
        )}

        <PasswordField
          label="Contraseña actual"
          autoComplete="current-password"
          autoFocus
          required
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />

        <PasswordField
          label="Nueva contraseña"
          autoComplete="new-password"
          required
          hint={`Mínimo ${PASSWORD_MIN_LENGTH} caracteres.`}
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />

        <PasswordStrengthMeter value={newPassword} />

        <PasswordField
          label="Repetir nueva contraseña"
          autoComplete="new-password"
          required
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Guardar contraseña'}
        </Button>

        <p className="tiny muted">
          No se permite reutilizar las últimas contraseñas de la cuenta. La verificación definitiva la
          realiza el servidor.
        </p>
      </form>
    </AuthShell>
  );
}
